/**
 * T3-1.1：图案裁剪服务端 Worker API 封装（submit / poll / retry）。
 * HTTP 层见 @/utils/api cropImageAPI（T3-1.2）；错误映射见 imageCropTaskApiError（T3-1.3）。
 */
import {
  cropImageAPI,
  IPE_TASK_RECORD_PAGE_SIZE,
  normalizeCropTaskSubmitPayload,
} from '@/utils/api';
import { expandCropRecordsFromPayload, normalizeCropImageRow } from '../utils/imageCropLoadResults';
import { buildCropImageSavePayload } from '../utils/imageCropRowStatus';
import { rememberImageCropResult } from '../utils/imageCropResultCache';
import {
  presentImageCropApiError,
  resolveImageCropFailedMessage,
  shouldMarkImageCropItemFailed,
} from '../utils/imageCropTaskApiError';
import { buildImageCropRetryRequest } from '../utils/imageCropRetryRoute';
import { IMAGE_CROP_WORKER_MODE } from '../constants/imageCropConfig';

function trimOrEmpty(value) {
  return value == null ? '' : String(value).trim();
}

async function runCropTaskApi(call, options = {}) {
  try {
    return await call();
  } catch (error) {
    throw presentImageCropApiError(error, options);
  }
}

function extractBatchRows(res) {
  if (!res || typeof res !== 'object') return [];
  if (Array.isArray(res.images) && res.images.length > 0) return res.images;
  if (Array.isArray(res.data) && res.data.length > 0) return res.data;
  return expandCropRecordsFromPayload(res);
}

function normalizePollBatchResponse(taskId, res, page) {
  const images = extractBatchRows(res).map((row) => normalizeCropImageRow(row)).filter(Boolean);
  return {
    taskId,
    page: Number(res?.page) || page,
    pageSize: Number(res?.pageSize) || IPE_TASK_RECORD_PAGE_SIZE,
    total: Number(res?.total) || images.length,
    totalPages: Number(res?.totalPages) || 1,
    images,
    raw: res,
  };
}

function normalizeRetryResponse(taskId, imageId, imageUrl, res) {
  const croppedImageUrl = trimOrEmpty(
    res?.croppedImageUrl ?? res?.cropped_image_url ?? '',
  );
  const croppedDisplayUrl = trimOrEmpty(
    res?.croppedImageUrlDisplay
    ?? res?.cropped_image_url_display
    ?? res?.croppedUrlDisplay
    ?? res?.viewUrl
    ?? res?.url
    ?? '',
  ) || croppedImageUrl;
  const status = croppedImageUrl || croppedDisplayUrl ? 'success' : 'failed';
  return {
    taskId,
    imageId,
    imageUrl,
    croppedImageUrl: croppedImageUrl || croppedDisplayUrl,
    croppedDisplayUrl,
    width: Number(res?.width) || 0,
    height: Number(res?.height) || 0,
    status,
    pipeline: res?.pipeline ?? null,
    usedFallback: Boolean(res?.usedFallback),
    cropPosition: res?.cropPosition ?? null,
    error: res?.error ?? null,
    raw: res,
  };
}

/**
 * 提交图案裁剪批任务（服务端 Worker 异步处理）。
 * @param {{ taskId: string, images: Array<{ imageId: string, imageUrl: string }>, mode?: string, force?: boolean }} params
 * @param {{ showToast?: (message: string, type?: string) => void }} [options]
 */
export async function submitImageCropTask(params, options = {}) {
  const payload = normalizeCropTaskSubmitPayload({
    ...params,
    mode: IMAGE_CROP_WORKER_MODE,
    force: Boolean(params?.force),
  });
  if (!payload.taskId) throw new Error('taskId is required');
  if (payload.images.length === 0) throw new Error('images is required');

  const res = await runCropTaskApi(
    () => cropImageAPI.submitTask(payload),
    options,
  );

  return {
    taskId: trimOrEmpty(res?.taskId) || payload.taskId,
    total: Number(res?.total) || payload.images.length,
    detectBatches: Number(res?.detectBatches) || 0,
    status: trimOrEmpty(res?.status) || 'queued',
    mode: payload.mode,
    raw: res,
  };
}

/**
 * 分页拉取任务裁剪记录（enriched，含 cropPosition）。
 * @param {string} taskId
 * @param {number} [page=1]
 * @param {{ showToast?: (message: string, type?: string) => void }} [options]
 */
export async function pollImageCropBatch(taskId, page = 1, options = {}) {
  const tid = trimOrEmpty(taskId);
  if (!tid) throw new Error('taskId is required');

  const safePage = Math.max(1, Number(page) || 1);
  const res = await runCropTaskApi(
    () => cropImageAPI.listBatchPage(tid, {
      page: safePage,
      pageSize: IPE_TASK_RECORD_PAGE_SIZE,
    }),
    options,
  );
  return normalizePollBatchResponse(tid, res, safePage);
}

/**
 * 单张重试（Qwen 批任务对齐）：
 * - 有 detect bbox（cropPosition.bboxNormalized）→ POST /crop-images/crop-by-bbox（跳过 Qwen 检测）
 * - 无 bbox → POST /crop-images/retry（Worker 路径：Qwen detect + crop）
 * 502/503 → 标记 failed，保留单张重试入口；429/403 → toast，不落 failed。
 * @param {{ taskId: string, imageId: string, imageUrl: string, cropPosition?: object | null }} params
 * @param {{ showToast?: (message: string, type?: string) => void }} [options]
 */
export async function retryImageCropItem(params, options = {}) {
  const tid = trimOrEmpty(params?.taskId);
  const iid = trimOrEmpty(params?.imageId);
  const url = trimOrEmpty(params?.imageUrl);
  if (!tid) throw new Error('taskId is required');
  if (!iid) throw new Error('imageId is required');
  if (!url) throw new Error('imageUrl is required');

  await cropImageAPI.save(
    tid,
    buildCropImageSavePayload({
      imageId: iid,
      originalImageUrl: url,
      status: 'processing',
    }),
  );

  const { strategy, call } = buildImageCropRetryRequest(cropImageAPI, {
    taskId: tid,
    imageId: iid,
    imageUrl: url,
    cropPosition: params?.cropPosition,
  });

  let res;
  try {
    res = await runCropTaskApi(call, options);
  } catch (error) {
    if (shouldMarkImageCropItemFailed(error)) {
      await cropImageAPI.save(
        tid,
        buildCropImageSavePayload({
          imageId: iid,
          originalImageUrl: url,
          status: 'failed',
          error: resolveImageCropFailedMessage(error, '重试失败'),
        }),
      );
    }
    throw error;
  }

  const normalized = normalizeRetryResponse(tid, iid, url, res);
  normalized.retryStrategy = strategy;
  if (normalized.croppedImageUrl) {
    rememberImageCropResult(tid, iid, {
      status: normalized.status,
      croppedUrl: normalized.croppedImageUrl,
      croppedDisplayUrl: normalized.croppedDisplayUrl,
      error: null,
    });
  }
  return normalized;
}
